import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Plus, Package, Loader2, Search, Pencil, Trash2, Calculator, AlertTriangle } from "lucide-react";
import { showError } from "../lib/errorHandler";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLASTIC_TYPES = [
  { value: "PP", label: "PP - Polipropileno" },
  { value: "PE", label: "PE - Polietileno" },
  { value: "ABS", label: "ABS" },
  { value: "PA", label: "PA - Poliamida" },
  { value: "PC", label: "PC - Policarbonato" },
  { value: "POM", label: "POM - Poliacetal" },
  { value: "PS", label: "PS - Poliestireno" },
  { value: "PVC", label: "PVC" },
  { value: "PET", label: "PET" },
  { value: "TPE", label: "TPE" },
];

const emptyForm = { code: "", description: "", plastic_type: "", lot_number: "", quantity_kg: "", quantity_available: "", supplier: "", min_stock_kg: "" };

function RecalcDialog({ open, onOpenChange, onApplied }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { setPreview(null); return; }
    setBusy(true);
    axios.post(`${API}/stock/recalc-historical?dry_run=true`)
      .then((r) => setPreview(r.data))
      .catch((e) => showError(e, "Erro na simulação"))
      .finally(() => setBusy(false));
  }, [open]);

  const apply = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/stock/recalc-historical?dry_run=false`);
      toast.success(`Recálculo aplicado: ${r.data.orders_changed} ordens e ${r.data.materials_changed} matérias-primas realinhadas`);
      onOpenChange(false);
      onApplied();
    } catch (e) { showError(e, "Erro ao aplicar recálculo"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="recalc-dialog">
        <DialogHeader>
          <DialogTitle className="font-chivo">Recálculo Histórico de Consumo MP</DialogTitle>
          <DialogDescription>Recalcula a MP consumida em todas as ordens a partir dos registos de produção e realinha o stock disponível. Pré-visualização antes de aplicar.</DialogDescription>
        </DialogHeader>
        {busy && !preview ? <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div> : preview && (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded border p-3 text-center"><p className="text-xs text-muted-foreground">Ordens analisadas</p><p className="text-2xl font-bold" data-testid="recalc-orders-checked">{preview.orders_checked}</p></div>
              <div className="rounded border p-3 text-center"><p className="text-xs text-muted-foreground">Ordens a corrigir</p><p className="text-2xl font-bold text-amber-600" data-testid="recalc-orders-changed">{preview.orders_changed}</p></div>
              <div className="rounded border p-3 text-center"><p className="text-xs text-muted-foreground">MP a realinhar</p><p className="text-2xl font-bold text-blue-600" data-testid="recalc-materials-changed">{preview.materials_changed}</p></div>
            </div>
            {preview.order_changes.length > 0 && (
              <div>
                <p className="font-semibold mb-1">Ordens</p>
                <div className="max-h-48 overflow-y-auto rounded border">
                  <table className="w-full text-xs"><thead className="bg-muted sticky top-0"><tr><th className="text-left p-2">Ordem</th><th className="text-right p-2">Peças</th><th className="text-right p-2">Antes (kg)</th><th className="text-right p-2">Depois (kg)</th></tr></thead>
                    <tbody>{preview.order_changes.map((c) => <tr key={c.order_id} className="border-t"><td className="p-2 font-mono">{c.code}</td><td className="p-2 text-right">{c.pieces}</td><td className="p-2 text-right text-red-600">{c.old_kg}</td><td className="p-2 text-right text-green-700 font-semibold">{c.new_kg}</td></tr>)}</tbody>
                  </table>
                </div>
              </div>
            )}
            {preview.material_changes.length > 0 && (
              <div>
                <p className="font-semibold mb-1">Matérias-primas</p>
                <div className="max-h-48 overflow-y-auto rounded border">
                  <table className="w-full text-xs"><thead className="bg-muted sticky top-0"><tr><th className="text-left p-2">Código</th><th className="text-right p-2">Consumido (kg)</th><th className="text-right p-2">Disp. antes</th><th className="text-right p-2">Disp. depois</th></tr></thead>
                    <tbody>{preview.material_changes.map((c) => <tr key={c.material_id} className="border-t"><td className="p-2 font-mono">{c.code}</td><td className="p-2 text-right">{c.consumed_kg}</td><td className="p-2 text-right text-red-600">{c.old_available}</td><td className="p-2 text-right text-green-700 font-semibold">{c.new_available}</td></tr>)}</tbody>
                  </table>
                </div>
              </div>
            )}
            {preview.orders_changed === 0 && preview.materials_changed === 0 && <p className="text-muted-foreground text-center py-2" data-testid="recalc-nothing">Tudo alinhado — nada a corrigir.</p>}
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Fechar</Button>
              <Button onClick={apply} disabled={busy || (preview.orders_changed === 0 && preview.materials_changed === 0)} data-testid="recalc-apply-btn">
                {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Aplicar Recálculo
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function RawMaterials() {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [recalcOpen, setRecalcOpen] = useState(false);
  const { user } = useAuth();

  const fetchMaterials = async () => {
    try {
      const response = await axios.get(`${API}/raw-materials`);
      setMaterials(response.data);
    } catch (error) {
      toast.error("Erro ao carregar matérias-primas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMaterials(); }, []);

  const openCreate = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (material) => {
    setEditingId(material.id);
    setFormData({
      code: material.code,
      description: material.description,
      plastic_type: material.plastic_type,
      lot_number: material.lot_number,
      quantity_kg: String(material.quantity_kg),
      quantity_available: String(material.quantity_available ?? material.quantity_kg),
      supplier: material.supplier || "",
      min_stock_kg: String(material.min_stock_kg ?? 0),
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.code || !formData.description || !formData.plastic_type || !formData.lot_number || !formData.quantity_kg) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }

    try {
      if (editingId) {
        await axios.put(`${API}/raw-materials/${editingId}`, {
          code: formData.code,
          description: formData.description,
          plastic_type: formData.plastic_type,
          lot_number: formData.lot_number,
          quantity_kg: parseFloat(formData.quantity_kg),
          quantity_available: parseFloat(formData.quantity_available || formData.quantity_kg),
          supplier: formData.supplier || null,
          min_stock_kg: parseFloat(formData.min_stock_kg || 0),
        });
        toast.success("Matéria-prima atualizada");
      } else {
        await axios.post(`${API}/raw-materials`, {
          code: formData.code,
          description: formData.description,
          plastic_type: formData.plastic_type,
          lot_number: formData.lot_number,
          quantity_kg: parseFloat(formData.quantity_kg),
          supplier: formData.supplier || null,
          min_stock_kg: parseFloat(formData.min_stock_kg || 0),
        });
        toast.success("Matéria-prima adicionada");
      }
      setDialogOpen(false);
      fetchMaterials();
    } catch (error) {
      showError(error, "Erro ao guardar");
    }
  };

  const handleDelete = async (id, code) => {
    if (!window.confirm(`Tem a certeza que deseja eliminar ${code}?`)) return;
    try {
      await axios.delete(`${API}/raw-materials/${id}`);
      toast.success("Matéria-prima eliminada");
      fetchMaterials();
    } catch (error) {
      showError(error, "Erro ao eliminar");
    }
  };

  const filteredMaterials = materials.filter(
    (m) =>
      m.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.lot_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.plastic_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (m.supplier || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStockStatus = (available, total, minStock = 0) => {
    if (total === 0) return { label: "Vazio", color: "bg-red-500" };
    if (minStock > 0 && available <= minStock) return { label: "Abaixo Mínimo", color: "bg-red-600" };
    const pct = (available / total) * 100;
    if (pct <= 10) return { label: "Crítico", color: "bg-red-500" };
    if (pct <= 30) return { label: "Baixo", color: "bg-amber-500" };
    return { label: "Normal", color: "bg-green-500" };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="raw-materials-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-chivo text-3xl md:text-4xl font-bold tracking-tight text-foreground">Matéria-Prima</h1>
          <p className="text-muted-foreground mt-1">Gestão de stock de matérias-primas e lotes</p>
        </div>
        <div className="flex gap-2">
          {user?.role === "admin" && (
            <Button variant="outline" onClick={() => setRecalcOpen(true)} data-testid="recalc-historical-btn">
              <Calculator className="h-4 w-4 mr-2" />
              Recalcular Histórico MP
            </Button>
          )}
          <Button onClick={openCreate} data-testid="add-material-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nova Matéria-Prima
          </Button>
        </div>
      </div>

      <RecalcDialog open={recalcOpen} onOpenChange={setRecalcOpen} onApplied={fetchMaterials} />

      {/* Dialog Criar/Editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-chivo text-foreground">
              {editingId ? "Editar Matéria-Prima" : "Nova Matéria-Prima"}
            </DialogTitle>
            <DialogDescription>
              {editingId ? "Altere os dados e guarde" : "Registe uma nova entrada de matéria-prima"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="code">Código *</Label>
                <Input id="code" value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })} placeholder="Ex: PP-001" required className="font-mono" data-testid="material-code" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lot_number">N Lote *</Label>
                <Input id="lot_number" value={formData.lot_number} onChange={(e) => setFormData({ ...formData, lot_number: e.target.value.toUpperCase() })} placeholder="Ex: LOT-2024-001" required className="font-mono" data-testid="material-lot" />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descrição *</Label>
              <Input id="description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Ex: Polipropileno Natural" required data-testid="material-description" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="plastic_type">Tipo Plástico *</Label>
                <select id="plastic_type" value={formData.plastic_type} onChange={(e) => setFormData({ ...formData, plastic_type: e.target.value })} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" required data-testid="material-type">
                  <option value="">Selecione...</option>
                  {PLASTIC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="quantity_kg">Quantidade Total (kg) *</Label>
                <Input id="quantity_kg" type="number" step="0.1" min="0" value={formData.quantity_kg} onChange={(e) => setFormData({ ...formData, quantity_kg: e.target.value })} placeholder="500" required data-testid="material-quantity" />
              </div>
            </div>

            {editingId && (
              <div className="space-y-2">
                <Label htmlFor="quantity_available">Stock Disponível (kg)</Label>
                <Input id="quantity_available" type="number" step="0.1" min="0" value={formData.quantity_available} onChange={(e) => setFormData({ ...formData, quantity_available: e.target.value })} placeholder="Stock atual" data-testid="material-available" />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="supplier">Fornecedor</Label>
                <Input id="supplier" value={formData.supplier} onChange={(e) => setFormData({ ...formData, supplier: e.target.value })} placeholder="Ex: Fornecedor ABC" data-testid="material-supplier" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="min_stock_kg">Stock Mínimo (kg)</Label>
                <Input id="min_stock_kg" type="number" step="0.1" min="0" value={formData.min_stock_kg} onChange={(e) => setFormData({ ...formData, min_stock_kg: e.target.value })} placeholder="Alerta abaixo de..." data-testid="material-min-stock" />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="save-material-btn">{editingId ? "Guardar" : "Adicionar"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-border">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Total Lotes</p>
              <p className="text-3xl font-bold text-foreground">{materials.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Stock Total (kg)</p>
              <p className="text-3xl font-bold text-blue-600">
                {materials.reduce((sum, m) => sum + (m.quantity_available ?? m.quantity_kg), 0).toFixed(0)}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Tipos Plástico</p>
              <p className="text-3xl font-bold text-green-600">
                {[...new Set(materials.map(m => m.plastic_type))].length}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Stock Crítico / Abaixo Mínimo</p>
              <p className="text-3xl font-bold text-red-600" data-testid="critical-stock-count">
                {materials.filter(m => {
                  const available = m.quantity_available ?? m.quantity_kg;
                  return (m.min_stock_kg > 0 && available <= m.min_stock_kg) || (m.quantity_kg > 0 && (available / m.quantity_kg) * 100 <= 10);
                }).length}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Pesquisar por código, descrição, lote..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9" data-testid="search-materials" />
      </div>

      {/* Materials Table */}
      <Card className="border-border">
        <CardHeader>
          <CardTitle className="font-chivo flex items-center gap-2 text-foreground">
            <Package className="h-5 w-5" />
            Stock de Matéria-Prima
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Código</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Lote</TableHead>
                  <TableHead>Quantidade (kg)</TableHead>
                  <TableHead>Disponível (kg)</TableHead>
                  <TableHead>Mínimo (kg)</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Fornecedor</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredMaterials.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                      {searchTerm ? "Nenhuma matéria-prima encontrada" : "Nenhuma matéria-prima registada"}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredMaterials.map((material) => {
                    const available = material.quantity_available ?? material.quantity_kg;
                    const status = getStockStatus(available, material.quantity_kg, material.min_stock_kg || 0);
                    return (
                      <TableRow key={material.id} data-testid={`material-row-${material.code}`}>
                        <TableCell className="font-mono font-bold text-foreground">{material.code}</TableCell>
                        <TableCell className="text-foreground">{material.description}</TableCell>
                        <TableCell><Badge variant="outline" className="font-mono">{material.plastic_type}</Badge></TableCell>
                        <TableCell className="font-mono text-muted-foreground">{material.lot_number}</TableCell>
                        <TableCell className="text-foreground">{material.quantity_kg.toFixed(1)}</TableCell>
                        <TableCell className="font-bold text-foreground">{available.toFixed(1)}</TableCell>
                        <TableCell className="text-muted-foreground">{material.min_stock_kg > 0 ? material.min_stock_kg.toFixed(1) : "-"}</TableCell>
                        <TableCell><Badge className={`${status.color} text-white`} data-testid={`material-status-${material.code}`}>{status.label === "Abaixo Mínimo" && <AlertTriangle className="h-3 w-3 mr-1 inline" />}{status.label}</Badge></TableCell>
                        <TableCell className="text-muted-foreground">{material.supplier || "-"}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(material)} data-testid={`edit-material-${material.code}`}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => handleDelete(material.id, material.code)} data-testid={`delete-material-${material.code}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
